import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import translations from '@/lib/translations';

// Available languages configuration
export const LANGUAGES = [
  { code: 'et', name: 'Eesti', flag: '🇪🇪' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
];

const LanguageContext = createContext({
  language: 'et',
  setLanguage: () => {},
  t: (key) => key,
});

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('workplus_language');
    if (saved && ['en', 'ru', 'et'].includes(saved)) {
      return saved;
    }
    
    // Default to Estonian
    return 'et';
  });

  const setLanguage = useCallback((lang) => {
    if (['en', 'ru', 'et'].includes(lang)) {
      setLanguageState(lang);
      localStorage.setItem('workplus_language', lang);
    }
  }, []);

  // Translation function
  const t = useCallback((key, params = {}) => {
    const translation = translations[language]?.[key] || translations['en']?.[key] || key;
    
    // Replace params like {name} with actual values
    if (Object.keys(params).length > 0) {
      return Object.entries(params).reduce((str, [key, value]) => {
        return str.replace(new RegExp(`{${key}}`, 'g'), value);
      }, translation);
    }
    
    return translation;
  }, [language]);

  const value = {
    language,
    setLanguage,
    t,
    isRussian: language === 'ru',
    isEnglish: language === 'en',
    isEstonian: language === 'et',
    languages: LANGUAGES,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Helper hook for getting status translations
export const useStatusLabel = () => {
  const { t } = useLanguage();
  
  return (status) => {
    return t(`status.${status}`) || status;
  };
};

// Helper hook for getting role translations
export const useRoleLabel = () => {
  const { t } = useLanguage();
  
  return (role) => {
    return t(`role.${role}`) || role;
  };
};
